import {
  withLifecycleCallbacks,
  type DataProvider,
  type GetListParams,
  type Identifier,
  type ResourceCallbacks,
} from "ra-core";
import { supabaseDataProvider } from "ra-supabase-core";
import type { ConfigurationContextValue } from "../../root/ConfigurationContext";
import { defaultConfiguration } from "../../root/defaultConfiguration";
import type {
  ContactNote,
  RAFile,
  Sale,
  SalesFormData,
  SignUpData,
} from "../../types";
import { getActivityLog } from "../commons/activity";
import { getIsInitialized } from "./authProvider";
import { SUPABASE_ANON_KEY, SUPABASE_URL, supabase } from "./supabase";
import { VENDOR_DOCUMENT_CHECKLIST } from "../../vendors/vendorDocumentChecklist";

const primaryKeys = new Map<string, string[]>();
primaryKeys.set("permissions", ["slug"]);
primaryKeys.set("roles", ["slug"]);
primaryKeys.set("role_permissions", ["role_slug", "permission_slug"]);
primaryKeys.set("uqc_codes", ["code"]);

const baseDataProvider = supabaseDataProvider({
  instanceUrl: SUPABASE_URL,
  apiKey: SUPABASE_ANON_KEY,
  supabaseClient: supabase,
  sortOrder: "asc,desc.nullslast" as any,
  primaryKeys,
});

/**
 * Strips all keys starting with an underscore from an object.
 * Used for UI-only fields that shouldn't be sent to Supabase.
 */
const stripInternalFields = (data: any) => {
  if (!data || typeof data !== "object") return data;
  const result = { ...data };
  Object.keys(result).forEach((key) => {
    if (key.startsWith("_")) {
      delete result[key];
    }
  });
  return result;
};

// Resources whose create/edit forms carry editable Bill To / Ship To addresses
// that are auto-filled from the company master.
const ADDRESS_DOC_RESOURCES = new Set(["quotations", "proforma_invoices"]);

// TC-06: when one of these documents is saved, a Bill To / Ship To that is
// nullish (cleared, or omitted by the form when emptied) is stored as an
// explicit "" so the document shows "Not provided" instead of silently falling
// back to the company master address. The create/edit forms always expose these
// fields, so an empty value here is an intentional clear — not a "never set".
// Legacy rows already NULL in the DB are untouched unless re-saved, so their
// master-address fallback in the show view is preserved.
const coerceClearedAddresses = (resource: string, data: any) => {
  if (!ADDRESS_DOC_RESOURCES.has(resource) || !data) return data;
  const result = { ...data };
  for (const field of ["billing_address", "shipping_address"]) {
    if (result[field] === null || result[field] === undefined) {
      result[field] = "";
    }
  }
  return result;
};

/**
 * Documents whose `customer_name` column is NOT NULL (quotations,
 * proforma_invoices, customer_invoices) — plus sales_orders, where the column
 * is denormalised for the PDF. If the form submits without a name (e.g. created
 * from a project before the watcher resolved it), derive it from the linked
 * contact, then the company, so the insert never violates the NOT NULL
 * constraint. See bug tracker SO #22 / QTN-from-project #14.
 */
const CUSTOMER_NAME_DOCS = new Set([
  "quotations",
  "proforma_invoices",
  "customer_invoices",
  "sales_orders",
]);

const ensureCustomerName = async (resource: string, data: any) => {
  if (!CUSTOMER_NAME_DOCS.has(resource) || !data) return data;
  if (data.customer_name && String(data.customer_name).trim()) return data;

  const contactId = data.customer_id || data.contact_id;
  if (contactId) {
    const { data: contact } = await supabase
      .from("contacts")
      .select("first_name,last_name")
      .eq("id", contactId)
      .single();
    const name = contact
      ? `${contact.first_name || ""} ${contact.last_name || ""}`.trim()
      : "";
    if (name) return { ...data, customer_name: name };
  }

  if (data.company_id) {
    const { data: company } = await supabase
      .from("companies")
      .select("name")
      .eq("id", data.company_id)
      .single();
    if (company?.name) return { ...data, customer_name: company.name };
  }

  return data;
};

const processCompanyLogo = async (params: any) => {
  const logo = params.data.logo;

  if (logo?.rawFile instanceof File) {
    await uploadToBucket(logo);
  }

  return {
    ...params,
    data: {
      ...params.data,
      logo,
    },
  };
};

// `columns` is the writable column allowlist for each child item table (id and
// DB-managed timestamps omitted). The shared item-insert path filters every
// item object down to these columns before INSERT. This is the permanent fix
// for the recurring "items silently vanish on save" class of bug (D-11 hsn_code,
// DC-3 / BOM-1 tax_rate): the document forms share HSN/inventory pickers that
// setValue() extra fields (e.g. tax_rate) which only exist on SOME item tables.
// Inserting the form object verbatim made PostgREST reject the whole batch for
// any table missing that column, and the error was only console.error'd. Keep
// these lists in sync with the DB schema when adding item columns (a missing
// entry silently drops just that field, never the whole row).
const documentResourceMap: Record<
  string,
  { table: string; fk: string; orderField: string; columns: string[] }
> = {
  purchase_orders: {
    table: "po_items",
    fk: "po_id",
    orderField: "item_number",
    columns: ["po_id", "item_number", "description", "hsn_code", "quantity", "unit", "unit_price", "tax_rate", "tax_amount", "line_total", "percentage"],
  },
  sales_orders: {
    table: "sales_order_items",
    fk: "sales_order_id",
    orderField: "",
    columns: ["sales_order_id", "description", "hsn_code", "quantity", "unit", "unit_price", "discount_percentage", "tax_rate", "line_total", "sub_items"],
  },
  quotations: {
    table: "quotation_items",
    fk: "quotation_id",
    orderField: "item_number",
    columns: ["quotation_id", "item_number", "description", "hsn_code", "quantity", "unit", "unit_price", "cost_price", "tax_rate", "tax_amount", "line_total", "item_terms", "sub_items"],
  },
  proforma_invoices: {
    table: "pi_items",
    fk: "pi_id",
    orderField: "item_number",
    columns: ["pi_id", "item_number", "description", "hsn_code", "quantity", "unit", "unit_price", "tax_rate", "tax_amount", "line_total", "item_terms", "sub_items"],
  },
  customer_invoices: {
    table: "invoice_items",
    fk: "invoice_id",
    orderField: "item_number",
    columns: ["invoice_id", "item_number", "description", "hsn_code", "quantity", "unit", "unit_price", "tax_rate", "tax_amount", "line_total", "grn_id", "po_id"],
  },
  delivery_challans: {
    table: "dc_items",
    fk: "dc_id",
    orderField: "sr_no",
    columns: ["dc_id", "sr_no", "description", "sub_items", "quantity", "unit", "hsn_code", "item_id", "tax_rate"],
  },
  vendor_bills: {
    table: "vendor_bill_items",
    fk: "bill_id",
    orderField: "id",
    columns: ["bill_id", "item_description", "quantity", "unit_price", "tax_rate", "tax_amount", "line_total"],
  },
  boms: {
    table: "bom_items",
    fk: "bom_id",
    orderField: "",
    columns: ["bom_id", "description", "quantity", "unit", "estimated_unit_cost", "total_cost", "notes", "hsn_code", "tax_rate"],
  },
};

// Strip an item object down to the columns that actually exist on its child
// table, so stray form fields can never break the batch INSERT. The fk and
// orderField are present in each `columns` list, so the code-set values survive.
const filterItemColumns = (
  item: Record<string, any>,
  mapping: { columns: string[] },
): Record<string, any> =>
  Object.fromEntries(
    Object.entries(item).filter(([key]) => mapping.columns.includes(key)),
  );

const appendItemsMeta = (resource: string, params: any) => {
  const mapping = documentResourceMap[resource];
  if (mapping) {
    let columns = `*,items:${mapping.table}(*)`;
    if (resource === "purchase_orders") {
      columns = `*,items:${mapping.table}(*),payment_terms:po_payment_terms(*)`;
    }
    return {
      ...params,
      meta: {
        ...params.meta,
        // ra-data-postgrest intercepts query construction.
        // It specifically maps `meta.columns` to PostgREST's `select` parameter.
        columns,
      },
    };
  }
  return params;
};

const dataProviderWithCustomMethods = {
  ...baseDataProvider,
  async getList(resource: string, params: GetListParams) {
    if (resource === "companies")
      return baseDataProvider.getList("companies_summary", params);
    if (resource === "contacts")
      return baseDataProvider.getList("contacts_summary", params);
    if (resource === "employees")
      return baseDataProvider.getList("employees_summary", params);
    if (resource === "hr_payroll_runs")
      return baseDataProvider.getList("payroll_run_summary", params);
    if (resource === "hr_attendance" && params?.meta?.summary === true) {
      return baseDataProvider.getList("attendance_monthly_summary", params);
    }
    return baseDataProvider.getList(
      resource,
      appendItemsMeta(resource, params),
    );
  },
  async getOne(resource: string, params: any) {
    if (resource === "companies")
      return baseDataProvider.getOne("companies_summary", params);
    if (resource === "contacts")
      return baseDataProvider.getOne("contacts_summary", params);
    if (resource === "employees")
      return baseDataProvider.getOne("employees_summary", params);
    if (resource === "hr_payroll_runs")
      return baseDataProvider.getOne("payroll_run_summary", params);
    return baseDataProvider.getOne(resource, appendItemsMeta(resource, params));
  },
  async getMany(resource: string, params: any) {
    if (resource === "companies")
      return baseDataProvider.getMany("companies_summary", params);
    if (resource === "contacts")
      return baseDataProvider.getMany("contacts_summary", params);
    if (resource === "employees")
      return baseDataProvider.getMany("employees_summary", params);
    if (resource === "hr_payroll_runs")
      return baseDataProvider.getMany("payroll_run_summary", params);
    return baseDataProvider.getMany(
      resource,
      appendItemsMeta(resource, params),
    );
  },
  async getManyReference(resource: string, params: any) {
    if (resource === "companies")
      return baseDataProvider.getManyReference("companies_summary", params);
    if (resource === "contacts")
      return baseDataProvider.getManyReference("contacts_summary", params);
    if (resource === "employees")
      return baseDataProvider.getManyReference("employees_summary", params);
    if (resource === "hr_payroll_runs")
      return baseDataProvider.getManyReference("payroll_run_summary", params);
    if (resource === "hr_attendance" && params?.meta?.summary === true) {
      return baseDataProvider.getManyReference(
        "attendance_monthly_summary",
        params,
      );
    }
    return baseDataProvider.getManyReference(
      resource,
      appendItemsMeta(resource, params),
    );
  },

  async signUp({ email, password, first_name, last_name }: SignUpData) {
    const response = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          first_name,
          last_name,
        },
      },
    });

    if (!response.data?.user || response.error) {
      console.error("signUp.error", response.error);
      throw new Error(response?.error?.message || "Failed to create account");
    }

    // Update the is initialized cache
    (getIsInitialized as any)._is_initialized_cache = true;

    return {
      id: response.data.user.id,
      email,
      password,
    };
  },
  async salesCreate(body: SalesFormData) {
    const { data, error } = await supabase.functions.invoke<{ data: Sale }>(
      "users",
      {
        method: "POST",
        body,
      },
    );

    if (!data || error) {
      console.error("salesCreate.error", error);
      const errorDetails = await (async () => {
        try {
          return (await error?.context?.json()) ?? {};
        } catch {
          return {};
        }
      })();
      throw new Error(errorDetails?.message || "Failed to create the user");
    }

    return data.data;
  },
  async salesUpdate(
    id: Identifier,
    data: Partial<Omit<SalesFormData, "password">>,
  ) {
    const {
      email,
      first_name,
      last_name,
      administrator,
      avatar,
      disabled,
      department,
      role,
      phone,
    } = data;

    const { data: updatedData, error } = await supabase.functions.invoke<{
      data: Sale;
    }>("users", {
      method: "PATCH",
      body: {
        sales_id: id,
        email,
        first_name,
        last_name,
        administrator,
        disabled,
        avatar,
        department,
        role,
        phone,
      },
    });

    if (!updatedData || error) {
      console.error("salesCreate.error", error);
      throw new Error("Failed to update account manager");
    }

    return updatedData.data;
  },
  async updatePassword(id: Identifier) {
    const { data: passwordUpdated, error } =
      await supabase.functions.invoke<boolean>("update_password", {
        method: "PATCH",
        body: {
          sales_id: id,
        },
      });

    if (!passwordUpdated || error) {
      console.error("update_password.error", error);
      throw new Error("Failed to update password");
    }

    return passwordUpdated;
  },
  async getActivityLog(companyId?: Identifier) {
    return getActivityLog(baseDataProvider, companyId);
  },
  async isInitialized() {
    return getIsInitialized();
  },
  async mergeContacts(sourceId: Identifier, targetId: Identifier) {
    const { data, error } = await supabase.functions.invoke("merge_contacts", {
      method: "POST",
      body: { loserId: sourceId, winnerId: targetId },
    });

    if (error) {
      console.error("merge_contacts.error", error);
      throw new Error("Failed to merge contacts");
    }

    return data;
  },
  async sendEmail(
    _resource: string,
    params: {
      id: Identifier;
      email: string;
      subject: string;
      message: string;
      attachment?: any;
      category?: "internal" | "customer";
    },
  ) {
    const { data, error } = await supabase.functions.invoke("send-email", {
      method: "POST",
      body: {
        to: params.email,
        subject: params.subject,
        body: params.message,
        attachment: params.attachment,
        category: params.category ?? "customer",
      },
    });

    if (error) {
      console.error("sendEmail.error", error);
      throw new Error("Failed to send email");
    }

    return data;
  },
  async sendTemplatedEmail(params: {
    templateSlug: string;
    toEmail: string;
    ccEmail?: string;
    payload?: Record<string, unknown>;
    category?: "internal" | "customer";
    attachment?: { name: string; content: string; type?: string };
  }) {
    const { data, error } = await supabase.functions.invoke(
      "send-templated-email",
      {
        method: "POST",
        body: {
          templateSlug: params.templateSlug,
          toEmail: params.toEmail,
          ccEmail: params.ccEmail,
          payload: params.payload ?? {},
          category: params.category,
          attachment: params.attachment,
        },
      },
    );
    if (error) {
      console.error("sendTemplatedEmail.error", error);
      throw new Error("Failed to send templated email");
    }
    return data;
  },
  async autoInvoiceFromDC(dcIds: Identifier[]) {
    const { data, error } = await supabase.functions.invoke(
      "auto-invoice-from-dc",
      {
        method: "POST",
        body: { dcIds },
      },
    );
    if (error) {
      console.error("autoInvoiceFromDC.error", error);
      throw new Error("Failed to generate invoices from DCs");
    }
    return data as { invoiceIds: number[]; errors: { dcId: number; error: string }[] };
  },
  async allocateReceipt(params: {
    receiptId: Identifier;
    invoiceIds?: Identifier[];
    mode?: "manual" | "fifo";
  }) {
    const { data, error } = await supabase.functions.invoke(
      "allocate-receipt",
      {
        method: "POST",
        body: {
          receiptId: params.receiptId,
          invoiceIds: params.invoiceIds ?? [],
          mode: params.mode ?? "manual",
        },
      },
    );
    if (error) {
      console.error("allocateReceipt.error", error);
      throw new Error("Failed to allocate receipt");
    }
    return data as {
      allocations: { invoice_id: number; allocated_amount: number }[];
    };
  },
  async postCreditNote(creditNoteId: Identifier) {
    const { data, error } = await supabase.functions.invoke(
      "post-credit-note",
      {
        method: "POST",
        body: { creditNoteId },
      },
    );
    if (error) {
      console.error("postCreditNote.error", error);
      throw new Error("Failed to post credit note");
    }
    return data;
  },
  async getConfiguration(): Promise<ConfigurationContextValue> {
    try {
      const { data } = await baseDataProvider.getOne("configuration", { id: 1 });
      return (data?.config as ConfigurationContextValue) ?? defaultConfiguration;
    } catch (error: any) {
      console.warn("Failed to fetch configuration, using defaults:", error?.message || error);
      // Return default configuration as fallback for unauthenticated users or errors
      return defaultConfiguration;
    }
  },
  async updateConfiguration(
    config: ConfigurationContextValue,
  ): Promise<ConfigurationContextValue> {
    const { data } = await baseDataProvider.update("configuration", {
      id: 1,
      data: { config },
      previousData: { id: 1 },
    });
    return data.config as ConfigurationContextValue;
  },
} satisfies DataProvider;

export type CrmDataProvider = typeof dataProviderWithCustomMethods;

const processConfigLogo = async (logo: any): Promise<string> => {
  if (typeof logo === "string") return logo;
  if (logo?.rawFile instanceof File) {
    await uploadToBucket(logo);
    return logo.src;
  }
  return logo?.src ?? "";
};

const lifeCycleCallbacks: ResourceCallbacks[] = [
  {
    resource: "configuration",
    beforeUpdate: async (params) => {
      const config = params.data.config;
      if (config) {
        config.lightModeLogo = await processConfigLogo(config.lightModeLogo);
        config.darkModeLogo = await processConfigLogo(config.darkModeLogo);
      }
      return params;
    },
  },
  {
    resource: "contact_notes",
    beforeSave: async (data: ContactNote, _, __) => {
      if (data.attachments) {
        data.attachments = await Promise.all(
          data.attachments.map((fi) => uploadToBucket(fi)),
        );
      }
      return data;
    },
  },
  {
    resource: "sales",
    beforeSave: async (data: Sale, _, __) => {
      if (data.avatar) {
        await uploadToBucket(data.avatar);
      }
      return data;
    },
  },
  {
    // Enquiry #18: upload enquiry attachments (drawings / PDFs / images)
    resource: "enquiries",
    beforeSave: async (data: any, _, __) => {
      if (Array.isArray(data.attachments)) {
        data.attachments = await Promise.all(
          data.attachments.map((fi: any) =>
            fi?.rawFile instanceof File ? uploadToBucket(fi) : fi,
          ),
        );
      }
      return data;
    },
  },
  {
    // Vendor #23: rebuild the document checklist from form rows (indexed by the
    // static checklist), upload any new files, and persist only populated rows.
    resource: "vendors",
    beforeSave: async (data: any, _, __) => {
      if (Array.isArray(data.documents)) {
        const rows = await Promise.all(
          data.documents.map(async (row: any, i: number) => {
            const meta = VENDOR_DOCUMENT_CHECKLIST[i];
            if (!meta || !row) return null;
            let fileUrl = row.file_url ?? null;
            if (row.file?.rawFile instanceof File) {
              const uploaded = await uploadToBucket(row.file);
              fileUrl = uploaded.src;
            }
            const remarks = row.remarks ?? "";
            if (!fileUrl && !remarks) return null;
            return {
              doc_type: meta.doc_type,
              doc_name: meta.doc_name,
              mandatory: meta.mandatory,
              file_url: fileUrl,
              remarks,
            };
          }),
        );
        data.documents = rows.filter(Boolean);
      }
      return data;
    },
  },
  {
    resource: "contacts",
    beforeGetList: async (params) =>
      applyFullTextSearch([
        "first_name",
        "last_name",
        "company_name",
        "title",
        "email",
        "phone",
        "background",
      ])(params),
  },
  {
    resource: "companies",
    beforeGetList: async (params) =>
      applyFullTextSearch([
        "name",
        "phone_number",
        "website",
        "zipcode",
        "city",
        "state_abbr",
      ])(params),
    beforeCreate: async (params) => {
      const createParams = await processCompanyLogo(params);
      return {
        ...createParams,
        data: {
          created_at: new Date().toISOString(),
          ...createParams.data,
        },
      };
    },
    beforeUpdate: async (params) => {
      return await processCompanyLogo(params);
    },
  },
  {
    resource: "contacts_summary",
    beforeGetList: async (params) =>
      applyFullTextSearch(["first_name", "last_name"])(params),
    beforeGetManyReference: async (params) =>
      applyFullTextSearch(["first_name", "last_name"])(params as any),
  },
  // Document-specific handlers and search mapping
  ...Object.keys(documentResourceMap).map((resource) => {
    const searchCols = {
      purchase_orders: ["reference", "vnd_code"],
      projects: ["reference", "name"],
      vendors: ["name", "code", "email", "phone"],
      rfqs: ["reference", "title"],
    }[resource] || ["reference"];

    return {
      resource,
      beforeGetList: async (params: any) => {
        const p = applyFullTextSearch(searchCols)(params);
        // Special logic for customer invoices status filters
        if (
          resource === "customer_invoices" &&
          (p.filter?.status === "overdue" || p.filter?.status === "due_soon")
        ) {
          const today = new Date();
          const todayStr = today.toISOString().split("T")[0];
          const { status, ...restFilter } = p.filter;
          if (status === "overdue") {
            p.filter = {
              ...restFilter,
              "due_date@lt": todayStr,
              "status@neq": "paid",
            };
          } else {
            const soon = new Date();
            soon.setDate(today.getDate() + 2);
            p.filter = {
              ...restFilter,
              "due_date@gte": todayStr,
              "due_date@lte": soon.toISOString().split("T")[0],
              "status@neq": "paid",
            };
          }
        }
        return p;
      },
      beforeGetManyReference: async (params: any) =>
        applyFullTextSearch(searchCols)(params as any),
      beforeCreate: async (params: any) => {
        // Handle file uploads for sales_orders. file_url_upload is a virtual
        // FileInput field, not a column — always strip it (even when empty/null)
        // so it never reaches the INSERT and triggers a schema-cache error.
        if (resource === "sales_orders" && "file_url_upload" in params.data) {
          if (params.data.file_url_upload?.rawFile) {
            const uploaded = await uploadToBucket(params.data.file_url_upload);
            params.data.file_url = uploaded.src;
          }
          delete params.data.file_url_upload;
        }

        // Guarantee a non-empty customer_name for docs that require it
        params.data = await ensureCustomerName(resource, params.data);

        // TC-06: persist a cleared Bill To / Ship To as an explicit empty string
        // instead of NULL. The create form always auto-fills these from the
        // company master, so an empty value here means the user intentionally
        // cleared it — the document must then show "Not provided", not silently
        // fall back to the master address (which the show view does for NULL,
        // to support legacy records created before these fields existed).
        params.data = coerceClearedAddresses(resource, params.data);

        // Handle items for all documentation
        if (params.data.items) {
          params.meta = { ...params.meta, _items: params.data.items };
          const { items: _items, ...dataWithoutItems } = params.data;
          params.data = dataWithoutItems;
        }
        // Handle payment_terms for purchase_orders
        if (resource === "purchase_orders" && params.data.payment_terms) {
          params.meta = {
            ...params.meta,
            _payment_terms: params.data.payment_terms,
          };
          const { payment_terms: _payment_terms, ...dataWithoutPT } =
            params.data;
          params.data = dataWithoutPT;
        }

        // Strip virtual fields for quotations
        if (resource === "quotations") {
          const {
            base_quotation_id: _bqid,
            revision_number: _rnum,
            ...dataWithStrippedFields
          } = params.data;
          params.data = dataWithStrippedFields;
        }

        // Duplicate check for vendor bills
        if (resource === "vendor_bills") {
          const { vendor_id, total_amount, bill_date } = params.data;
          if (vendor_id && total_amount && bill_date) {
            const { data: existing } = await supabase
              .from("vendor_bills")
              .select("id")
              .eq("vendor_id", vendor_id)
              .eq("bill_date", bill_date)
              .eq("total_amount", total_amount)
              .limit(1);
            if (
              existing?.length &&
              !window.confirm(
                `Duplicate Bill Detected! A bill from this vendor on date ${bill_date} for amount ${total_amount} already exists. Proceed?`,
              )
            ) {
              throw new Error("Duplicate Bill creation aborted by user.");
            }
          }
        }
        return params;
      },
      beforeUpdate: async (params: any) => {
        // Mirror the create-path file handling on edit: upload a newly chosen
        // file and always strip the virtual file_url_upload field.
        if (resource === "sales_orders" && "file_url_upload" in params.data) {
          if (params.data.file_url_upload?.rawFile) {
            const uploaded = await uploadToBucket(params.data.file_url_upload);
            params.data.file_url = uploaded.src;
          }
          delete params.data.file_url_upload;
        }
        // Keep customer_name populated on edit too (e.g. company changed)
        params.data = await ensureCustomerName(resource, params.data);
        // TC-06: a cleared Bill To / Ship To persists as "" (see beforeCreate).
        params.data = coerceClearedAddresses(resource, params.data);
        return params;
      },
    };
  }),
];

const handleDatabaseError = (error: any) => {
  const errorString =
    typeof error === "string"
      ? error
      : JSON.stringify(error) + (error?.message || "");
  if (
    errorString.includes("violates foreign key constraint") ||
    errorString.includes("still referenced")
  ) {
    throw new Error(
      "Cannot delete this record because it is referenced by other records.",
    );
  }
  throw error;
};

const dataProviderWithInternalStripping = {
  ...dataProviderWithCustomMethods,
  create: async (resource: string, params: any) => {
    params.data = stripInternalFields(params.data);

    // Guarantee items and payment_terms never reach the base query
    const items = params.data.items || params.meta?._items;
    const payment_terms =
      params.data.payment_terms || params.meta?._payment_terms;
    if ("items" in params.data) delete params.data.items;
    if ("payment_terms" in params.data) delete params.data.payment_terms;

    const result = await dataProviderWithCustomMethods.create(resource, params);

    const mapping = documentResourceMap[resource];

    if (items?.length && mapping) {
      const itemsToInsert = items.map((item: any, index: number) => {
        const { id: _id, ...itemData } = item;
        return filterItemColumns(
          {
            ...itemData,
            [mapping.fk]: result.data.id,
            ...(mapping.orderField ? { [mapping.orderField]: index + 1 } : {}),
          },
          mapping,
        );
      });
      const { error } = await supabase
        .from(mapping.table)
        .insert(itemsToInsert);
      if (error)
        console.error(`Failed to insert items for ${resource}:`, error);
      result.data.items = itemsToInsert;
    } else if (mapping && result.data.id) {
      const { data: existingItems } = await supabase
        .from(mapping.table)
        .select("*")
        .eq(mapping.fk, result.data.id);
      result.data.items = existingItems || [];
    }

    if (resource === "purchase_orders" && payment_terms?.length) {
      const termsToInsert = payment_terms.map((t: any, i: number) => {
        const { id: _id, ...td } = t;
        return { ...td, po_id: result.data.id, sr_no: i + 1 };
      });
      const { error } = await supabase
        .from("po_payment_terms")
        .insert(termsToInsert);
      if (error) console.error(`Failed to insert terms for PO:`, error);
      result.data.payment_terms = termsToInsert;
    }

    return result;
  },
  update: async (resource: string, params: any) => {
    params.data = stripInternalFields(params.data);

    // Guarantee items and payment_terms never reach the base query
    const items = params.data.items || params.meta?._items;
    const payment_terms =
      params.data.payment_terms || params.meta?._payment_terms;
    if ("items" in params.data) delete params.data.items;
    if ("payment_terms" in params.data) delete params.data.payment_terms;

    const result = await dataProviderWithCustomMethods.update(resource, params);

    const mapping = documentResourceMap[resource];
    if (items && Array.isArray(items) && mapping) {
      await supabase
        .from(mapping.table)
        .delete()
        .eq(mapping.fk, result.data.id);
      if (items.length > 0) {
        const itemsToInsert = items.map((item: any, index: number) => {
          const { id: _id, ...itemData } = item;
          return filterItemColumns(
            {
              ...itemData,
              [mapping.fk]: result.data.id,
              ...(mapping.orderField ? { [mapping.orderField]: index + 1 } : {}),
            },
            mapping,
          );
        });
        const { error } = await supabase
          .from(mapping.table)
          .insert(itemsToInsert);
        if (error)
          console.error(`Failed to update items for ${resource}:`, error);
        result.data.items = itemsToInsert;
      } else {
        result.data.items = [];
      }
    } else if (mapping && result.data.id) {
      const { data: existingItems } = await supabase
        .from(mapping.table)
        .select("*")
        .eq(mapping.fk, result.data.id);
      result.data.items = existingItems || [];
    }

    if (
      resource === "purchase_orders" &&
      payment_terms &&
      Array.isArray(payment_terms)
    ) {
      await supabase
        .from("po_payment_terms")
        .delete()
        .eq("po_id", result.data.id);
      if (payment_terms.length > 0) {
        const termsToInsert = payment_terms.map((t: any, i: number) => {
          const { id: _id, ...td } = t;
          return { ...td, po_id: result.data.id, sr_no: i + 1 };
        });
        const { error } = await supabase
          .from("po_payment_terms")
          .insert(termsToInsert);
        if (error) console.error(`Failed to update terms for PO:`, error);
        result.data.payment_terms = termsToInsert;
      } else {
        result.data.payment_terms = [];
      }
    } else if (resource === "purchase_orders" && result.data.id) {
      const { data: existingTerms } = await supabase
        .from("po_payment_terms")
        .select("*")
        .eq("po_id", result.data.id);
      result.data.payment_terms = existingTerms || [];
    }

    return result;
  },
  delete: async (resource: string, params: any) => {
    try {
      return await dataProviderWithCustomMethods.delete(resource, params);
    } catch (err: any) {
      handleDatabaseError(err);
      throw err;
    }
  },
  deleteMany: async (resource: string, params: any) => {
    try {
      return await dataProviderWithCustomMethods.deleteMany(resource, params);
    } catch (err: any) {
      handleDatabaseError(err);
      throw err;
    }
  },
};

export const dataProvider = withLifecycleCallbacks(
  dataProviderWithInternalStripping as any,
  lifeCycleCallbacks,
) as CrmDataProvider;

const applyFullTextSearch =
  (columns: string[]) =>
  <T extends { filter?: any }>(params: T): T => {
    if (!params.filter?.q) {
      return params;
    }
    const { q, ...filter } = params.filter;
    return {
      ...params,
      filter: {
        ...filter,
        "@or": columns.reduce((acc, column) => {
          if (column === "email")
            return {
              ...acc,
              [`email_fts@ilike`]: q,
            };
          if (column === "phone")
            return {
              ...acc,
              [`phone_fts@ilike`]: q,
            };
          else
            return {
              ...acc,
              [`${column}@ilike`]: q,
            };
        }, {}),
      },
    };
  };

const uploadToBucket = async (fi: RAFile) => {
  if (!fi.src.startsWith("blob:") && !fi.src.startsWith("data:")) {
    // Sign URL check if path exists in the bucket
    if (fi.path) {
      const { error } = await supabase.storage
        .from("attachments")
        .createSignedUrl(fi.path, 60);

      if (!error) {
        return fi;
      }
    }
  }

  const dataContent = fi.src
    ? await fetch(fi.src)
        .then((res) => {
          if (res.status !== 200) {
            return null;
          }
          return res.blob();
        })
        .catch(() => null)
    : fi.rawFile;

  if (dataContent == null) {
    // We weren't able to download the file from its src (e.g. user must be signed in on another website to access it)
    // or the file has no content (not probable)
    // In that case, just return it as is: when trying to download it, users should be redirected to the other website
    // and see they need to be signed in. It will then be their responsibility to upload the file back to the note.
    return fi;
  }

  const file = fi.rawFile;
  const fileParts = file.name.split(".");
  const fileExt = fileParts.length > 1 ? `.${file.name.split(".").pop()}` : "";
  const fileName = `${Math.random()}${fileExt}`;
  const filePath = `${fileName}`;
  const { error: uploadError } = await supabase.storage
    .from("attachments")
    .upload(filePath, dataContent);

  if (uploadError) {
    console.error("uploadError", uploadError);
    throw new Error("Failed to upload attachment");
  }

  const { data } = supabase.storage.from("attachments").getPublicUrl(filePath);

  fi.path = filePath;
  fi.src = data.publicUrl;

  // save MIME type
  const mimeType = file.type;
  fi.type = mimeType;

  return fi;
};
